import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { WalletReadyState, type WalletName } from '@solana/wallet-adapter-base';
import { ACCOUNT_COLORS } from '../../../components/account/accountPageStyles';
import { AccountButton } from '../../../components/account/AccountButton';
import { SOLANA_WALLET_CATALOG } from '../config/walletCatalog';
import { resolveWalletIcon, getWalletIconDisplay } from '../config/walletIcons';

interface WalletPickerListProps {
  open: boolean;
  onClose: () => void;
  adapterWallets: Array<{
    adapter: { name: WalletName; icon: string; url: string };
    readyState: WalletReadyState;
  }>;
  onSelect: (adapterName: WalletName) => void;
  connecting: boolean;
  connectingWallet?: WalletName | null;
  error?: { title: string; detail?: string } | null;
  onClearError?: () => void;
  title?: string;
  subtitle?: string;
}

export const WalletPickerList: React.FC<WalletPickerListProps> = ({
  open,
  onClose,
  adapterWallets,
  onSelect,
  connecting,
  connectingWallet,
  error,
  onClearError,
  title = 'Connect a Solana wallet',
  subtitle = 'Pick your wallet extension. You will sign a short message to verify ownership.',
}) => {
  const { installed, unavailable } = useMemo(() => {
    const installedList: Array<{
      entry: (typeof SOLANA_WALLET_CATALOG)[number];
      readyState: WalletReadyState;
      icon?: string;
      url: string;
    }> = [];
    const unavailableList: typeof installedList = [];

    for (const entry of SOLANA_WALLET_CATALOG) {
      const match = adapterWallets.find((w) => w.adapter.name === entry.adapterName);
      const readyState = match?.readyState ?? WalletReadyState.NotDetected;
      const row = {
        entry,
        readyState,
        icon: resolveWalletIcon(entry.slug, match?.adapter.icon) ?? entry.icon,
        url: match?.adapter.url ?? entry.installUrl,
      };
      if (readyState === WalletReadyState.Installed || readyState === WalletReadyState.Loadable) {
        installedList.push(row);
      } else {
        unavailableList.push(row);
      }
    }

    return { installed: installedList, unavailable: unavailableList };
  }, [adapterWallets]);

  const renderWalletRow = (
    row: (typeof installed)[number],
    options: { installed: boolean }
  ) => {
    const { entry, icon, url } = row;
    const isActive = connecting && connectingWallet === entry.adapterName;
    const isDisabled = connecting && !isActive;
    const display = getWalletIconDisplay(entry.slug);

    return (
      <Box
        key={entry.slug}
        component={options.installed ? 'button' : 'div'}
        type={options.installed ? 'button' : undefined}
        disabled={options.installed ? isDisabled : undefined}
        onClick={options.installed ? () => onSelect(entry.adapterName) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          textAlign: 'left',
          p: 1.75,
          borderRadius: '14px',
          border: `1px solid ${isActive ? ACCOUNT_COLORS.teal : ACCOUNT_COLORS.border}`,
          bgcolor: isActive ? 'rgba(0, 113, 133, 0.06)' : ACCOUNT_COLORS.surface,
          cursor: options.installed && !isDisabled ? 'pointer' : 'default',
          opacity: isDisabled ? 0.55 : 1,
          transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',
          ...(options.installed &&
            !isDisabled && {
              '&:hover': {
                borderColor: ACCOUNT_COLORS.borderStrong,
                bgcolor: ACCOUNT_COLORS.surfaceMuted,
                boxShadow: '0 2px 10px rgba(15, 23, 42, 0.07)',
              },
            }),
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: '14px',
            bgcolor: display.background === 'transparent' ? 'transparent' : ACCOUNT_COLORS.surfaceMuted,
            border: display.showBorder ? `1px solid ${ACCOUNT_COLORS.border}` : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: display.imageScale && display.imageScale > 1 ? 'visible' : 'hidden',
            p: display.padding,
          }}
        >
          {icon ? (
            <Box
              component="img"
              src={icon}
              alt=""
              sx={{
                width: `${(display.imageScale ?? 1) * 100}%`,
                height: `${(display.imageScale ?? 1) * 100}%`,
                objectFit: display.objectFit,
              }}
            />
          ) : (
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 26, color: ACCOUNT_COLORS.textMuted }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: ACCOUNT_COLORS.text, lineHeight: 1.3 }}>
            {entry.label}
          </Typography>
          {options.installed ? (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.4 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: '#22C55E',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '0.8125rem', color: ACCOUNT_COLORS.textMuted }}>
                {isActive ? 'Connecting…' : 'Ready to connect'}
              </Typography>
            </Stack>
          ) : (
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.35,
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: ACCOUNT_COLORS.teal,
                mt: 0.4,
              }}
            >
              Get extension
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </Box>

        {isActive ? (
          <CircularProgress size={22} sx={{ color: ACCOUNT_COLORS.teal, flexShrink: 0 }} />
        ) : options.installed ? (
          <ChevronRightRoundedIcon sx={{ color: ACCOUNT_COLORS.textSubtle, flexShrink: 0, fontSize: 24 }} />
        ) : null}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !connecting && onClose()}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 56px rgba(15, 23, 42, 0.18)',
          width: '92%',
          maxWidth: 440,
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          background: ACCOUNT_COLORS.walletAccent,
          color: '#fff',
          position: 'relative',
        }}
      >
        <IconButton
          size="small"
          onClick={onClose}
          disabled={connecting}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            color: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon />
        </IconButton>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ pr: 5 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              bgcolor: 'rgba(255,255,255,0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: ACCOUNT_COLORS.fontDisplay, fontWeight: 800, fontSize: '1.25rem', lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.82)', mt: 0.5, maxWidth: 320 }}>
              {subtitle}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2.5 }}>
        {error && (
          <Alert
            severity="error"
            onClose={onClearError}
            sx={{ mb: 2, borderRadius: '10px' }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>{error.title}</Typography>
            {error.detail && (
              <Typography sx={{ fontSize: '0.8125rem', mt: 0.25 }}>{error.detail}</Typography>
            )}
          </Alert>
        )}

        {installed.length > 0 && (
          <Box sx={{ mb: unavailable.length > 0 ? 2.5 : 0 }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: ACCOUNT_COLORS.textMuted,
                mb: 1.25,
              }}
            >
              Available ({installed.length})
            </Typography>
            <Stack spacing={1}>{installed.map((row) => renderWalletRow(row, { installed: true }))}</Stack>
          </Box>
        )}

        {unavailable.length > 0 && (
          <Box>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: ACCOUNT_COLORS.textMuted,
                mb: 1.25,
              }}
            >
              Install a wallet
            </Typography>
            <Stack spacing={1}>
              {unavailable.map((row) => renderWalletRow(row, { installed: false }))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2.5 }}>
          <AccountButton accountVariant="outline" onClick={onClose} disabled={connecting}>
            Cancel
          </AccountButton>
        </Stack>
      </Box>
    </Dialog>
  );
};
